import * as React from "react";
import type { ReactNode } from "react";

import { connect } from "react-redux";

import { MAIN_PROPS } from "app/common/keyword-research/keyword-generator/constants";
import {
    createCpcFilter,
    createDifficultyValue,
    createVolumeFilter,
    getRangeFilterQueryParamValue,
} from "pro-features/components/filtersPanel/src/RangeFilterUtilityFunctions";
import { i18nFilter } from "app/filters/i18nFilter";
import { booleanSearchToObject } from "app/pages/website-analysis/traffic-sources/search/booleanSearchUtility";
import type { IUserKeywordGroup } from "app/services/keyword-groups";
import { KeywordsGroupUtilities } from "app/UtilitiesAndConstants/UtilityFunctions/KeywordsGroupUtilities";

import type { IDurationData } from "../../../../../../services/DurationService";
import { BasicDurations } from "../../../../../../services/DurationService";

interface IKeywordGeneratorToolPageTableHeaderProps {
    onFilterChange: (filters: any, updateUrlOnChange: boolean) => void;
    country: number;
    seedKeyword: string;
    duration: string;
    getDurationApiParams: (duration: string) => IDurationData;
    volumeFromValue: string;
    volumeToValue: string;
    cpcFromValue: string;
    cpcToValue: string;
    difficultyFromValue: string;
    difficultyToValue: string;
    intentsfilter: string;
    children?: ReactNode;
}

const resolveKeywordsGroup = async (seedKeyword: string): Promise<IUserKeywordGroup | undefined> => {
    return (
        (await KeywordsGroupUtilities.getGroupById(seedKeyword)) ||
        (await KeywordsGroupUtilities.getGroupByName(seedKeyword))
    );
};

class KeywordGeneratorToolPageTableHeader extends React.Component<IKeywordGeneratorToolPageTableHeaderProps, {}> {
    context: any;
    private i18n;
    private timeoutId;

    constructor(props) {
        super(props);
        this.i18n = i18nFilter();
    }

    public componentDidUpdate(prevProps) {
        MAIN_PROPS.forEach((prop) => {
            if (this.props[prop] !== prevProps[prop]) {
                this.changeFilterDebounce(this.props);
            }
        });
    }

    public render() {
        return <div>{this.props.children}</div>;
    }

    private changeFilterDebounce(props) {
        if (this.timeoutId) {
            clearTimeout(this.timeoutId);
        }

        this.timeoutId = setTimeout(async () => {
            const {
                country,
                duration,
                seedKeyword,
                webSource,
                booleanSearchTerms,
                isGroupContext,
                volumeFromValue,
                volumeToValue,
                cpcFromValue,
                cpcToValue,
                difficultyFromValue,
                difficultyToValue,
                intentsfilter,
            } = props;

            const durationObject = this.props.getDurationApiParams(duration);
            const filter: { [key: string]: string | boolean } = {
                from: durationObject.forAPI.from, // 2018|08|01
                to: durationObject.forAPI.to, // 2018|11|30
                isWindow: durationObject.forAPI.isWindow,
                intentsfilter,
            };

            const volumeColumnName =
                duration === BasicDurations.LAST_TWENTY_EIGHT_DAYS ? "windowVolume" : "monthlyVolume";
            filter.rangeFilter = getRangeFilterQueryParamValue([
                createVolumeFilter(volumeFromValue, volumeToValue, volumeColumnName),
                createCpcFilter(cpcFromValue, cpcToValue, "cpc"),
                createDifficultyValue(difficultyFromValue, difficultyToValue),
            ]);
            if (booleanSearchTerms) {
                const { IncludeTerms, ExcludeTerms } = booleanSearchToObject(booleanSearchTerms);
                filter.ExcludeTerms = ExcludeTerms;
                filter.IncludeTerms = IncludeTerms;
            } else {
                filter.ExcludeTerms = null;
                filter.IncludeTerms = null;
            }
            if (isGroupContext) {
                const resolvedGroup = await resolveKeywordsGroup(seedKeyword);
                if (resolvedGroup) {
                    filter.GroupHash = resolvedGroup.GroupHash;
                    filter.keyword = resolvedGroup.Id;
                }
            }
            this.props.onFilterChange({ country, keyword: seedKeyword, websource: webSource, ...filter }, false);
            this.timeoutId = null;
        }, 50);
    }
}

const mapStateToProps = (props) => {
    const { keywordGeneratorTool } = props;
    const { seedKeyword = String(), country, duration, webSource, booleanSearchTerms } = keywordGeneratorTool;
    const isSeedKeywordStartsWithGroupPrefix = seedKeyword?.startsWith("*");
    const isGroupContext = keywordGeneratorTool.isGroupContext || isSeedKeywordStartsWithGroupPrefix;
    return {
        seedKeyword: isSeedKeywordStartsWithGroupPrefix ? seedKeyword.substring(1) : seedKeyword,
        country,
        duration,
        webSource,
        booleanSearchTerms,
        isGroupContext,
    };
};

export default connect(mapStateToProps)(KeywordGeneratorToolPageTableHeader as React.ComponentType<any>);
